import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardContent,
  CardMedia
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import moment from 'moment';
import axios from 'axios';
import KycStatusBadge from '../components/KycStatusBadge';

const documentTypeLabels = {
  passport: 'Passport',
  drivers_license: 'Driver\'s License',
  national_id: 'National ID',
  utility_bill: 'Utility Bill',
  bank_statement: 'Bank Statement'
};

const AdminKycPage = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewStatus, setReviewStatus] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://api.safenetbanking.com/api/kyc/admin/submissions', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmissions(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching KYC submissions:', err);
      setError('Failed to load KYC submissions. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const handleViewDocument = async (documentId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`https://api.safenetbanking.com/api/kyc/admin/document/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedDocument(response.data);
      setViewDialogOpen(true);
    } catch (err) {
      console.error('Error fetching document details:', err);
      alert('Failed to load document details');
    }
  };

  const handleReviewDocument = (document) => {
    setSelectedDocument(document);
    setReviewStatus('');
    setRejectionReason('');
    setReviewError('');
    setReviewDialogOpen(true);
  };

  const handleSubmitReview = async () => {
    if (!reviewStatus) {
      setReviewError('Please select a status');
      return;
    }

    if (reviewStatus === 'rejected' && !rejectionReason) {
      setReviewError('Please provide a rejection reason');
      return;
    }

    setReviewLoading(true);
    setReviewError('');

    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `https://api.safenetbanking.com/api/kyc/admin/document/${selectedDocument.id}`,
        {
          status: reviewStatus,
          rejection_reason: rejectionReason
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Close dialog and refresh data
      setReviewDialogOpen(false);
      fetchSubmissions();
    } catch (err) {
      console.error('Error updating document status:', err);
      setReviewError('Failed to update document status. Please try again.');
    } finally {
      setReviewLoading(false);
    }
  };

  const filteredSubmissions = submissions.filter(submission => {
    if (filterStatus === 'all') return true;
    return submission.status === filterStatus;
  });

  const renderViewDialog = () => {
    if (!selectedDocument) return null;

    const isImage = selectedDocument.file_path && 
      (selectedDocument.file_path.endsWith('.jpg') || 
       selectedDocument.file_path.endsWith('.jpeg') || 
       selectedDocument.file_path.endsWith('.png'));

    const isPdf = selectedDocument.file_path && selectedDocument.file_path.endsWith('.pdf');

    return (
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Document Details
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                User Information
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Name:</strong> {selectedDocument.full_name}
                </Typography>
                <Typography variant="body2">
                  <strong>Email:</strong> {selectedDocument.email}
                </Typography>
                <Typography variant="body2">
                  <strong>Account Number:</strong> {selectedDocument.account_number}
                </Typography>
              </Box>

              <Typography variant="subtitle1" gutterBottom>
                Document Information
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Type:</strong> {documentTypeLabels[selectedDocument.document_type] || selectedDocument.document_type}
                </Typography>
                <Typography variant="body2">
                  <strong>Document Number:</strong> {selectedDocument.document_number || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Expiry Date:</strong> {selectedDocument.expiry_date 
                    ? moment(selectedDocument.expiry_date).format('MMM DD, YYYY')
                    : 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Submitted:</strong> {moment(selectedDocument.created_at).format('MMM DD, YYYY HH:mm')}
                </Typography>
                <Typography variant="body2">
                  <strong>Status:</strong> <KycStatusBadge status={selectedDocument.status} />
                </Typography>
                {selectedDocument.status === 'rejected' && selectedDocument.rejection_reason && (
                  <Typography variant="body2">
                    <strong>Rejection Reason:</strong> {selectedDocument.rejection_reason}
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>
                Document Preview
              </Typography>
              <Card>
                {isImage && (
                  <CardMedia
                    component="img"
                    image={`https://api.safenetbanking.com${selectedDocument.file_path}`}
                    alt="Document"
                    sx={{ maxHeight: 400, objectFit: 'contain' }}
                  />
                )}
                {isPdf && (
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                      <Button 
                        variant="contained" 
                        href={`https://api.safenetbanking.com${selectedDocument.file_path}`}
                        target="_blank"
                      >
                        View PDF Document
                      </Button>
                    </Box>
                  </CardContent>
                )}
                {!isImage && !isPdf && (
                  <CardContent>
                    <Typography>
                      File preview not available
                    </Typography>
                  </CardContent>
                )}
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => {
              setViewDialogOpen(false);
              handleReviewDocument(selectedDocument);
            }}
          >
            Review Document
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderReviewDialog = () => {
    if (!selectedDocument) return null;

    return (
      <Dialog 
        open={reviewDialogOpen} 
        onClose={() => setReviewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Review Document
        </DialogTitle>
        <DialogContent>
          {reviewError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {reviewError}
            </Alert>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>User:</strong> {selectedDocument.full_name}
            </Typography>
            <Typography variant="body2">
              <strong>Document Type:</strong> {documentTypeLabels[selectedDocument.document_type] || selectedDocument.document_type}
            </Typography>
            <Typography variant="body2">
              <strong>Current Status:</strong> <KycStatusBadge status={selectedDocument.status} />
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Update Status</InputLabel>
            <Select
              value={reviewStatus}
              onChange={(e) => setReviewStatus(e.target.value)}
              label="Update Status"
            >
              <MenuItem value="approved">Approve</MenuItem>
              <MenuItem value="rejected">Reject</MenuItem>
            </Select>
          </FormControl>

          {reviewStatus === 'rejected' && (
            <TextField
              fullWidth
              label="Rejection Reason"
              multiline
              rows={3}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              required
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleSubmitReview}
            disabled={reviewLoading}
          >
            {reviewLoading ? <CircularProgress size={24} /> : 'Submit Review'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  if (loading && submissions.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        KYC Management
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            KYC Document Submissions
          </Typography>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Filter Status</InputLabel>
            <Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              label="Filter Status"
              size="small"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Account #</TableCell>
                <TableCell>Document Type</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSubmissions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No KYC submissions found
                  </TableCell>
                </TableRow>
              ) : (
                filteredSubmissions.map((submission) => (
                  <TableRow key={submission.id}>
                    <TableCell>{submission.full_name}</TableCell>
                    <TableCell>{submission.account_number}</TableCell>
                    <TableCell>
                      {documentTypeLabels[submission.document_type] || submission.document_type}
                    </TableCell>
                    <TableCell>
                      {moment(submission.created_at).format('MMM DD, YYYY')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
                        color={
                          submission.status === 'approved'
                            ? 'success'
                            : submission.status === 'rejected'
                            ? 'error'
                            : 'warning'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleViewDocument(submission.id)}
                      >
                        View
                      </Button>
                      {submission.status === 'pending' && (
                        <>
                          <Button
                            size="small"
                            color="success"
                            startIcon={<CheckCircleIcon />}
                            onClick={() => {
                              setSelectedDocument(submission);
                              setReviewStatus('approved');
                              setRejectionReason('');
                              setReviewDialogOpen(true);
                            }}
                            sx={{ ml: 1 }}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<CancelIcon />}
                            onClick={() => {
                              setSelectedDocument(submission);
                              setReviewStatus('rejected');
                              setRejectionReason('');
                              setReviewDialogOpen(true);
                            }}
                            sx={{ ml: 1 }}
                          >
                            Reject
                          </Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {renderViewDialog()}
      {renderReviewDialog()}
    </Box>
  );
};

export default AdminKycPage;
